import { lazy, type LazyExoticComponent, type ComponentType } from 'react';

/**
 * Module Registry — punto único donde se declaran los módulos del shell.
 *
 * Cada módulo es lazy-loaded con `React.lazy(import())` y declara:
 *  - id: clave usada en el claim `modules` del JWT (debe matchear el backend)
 *  - i18nKey: clave de traducción para el nombre en el menú
 *  - path: ruta base del módulo en React Router
 *  - icon: emoji/símbolo (placeholder, sustituible por SVG/Heroicons)
 *  - requiredRoles: opcional, restringe el acceso a roles concretos. Si está
 *    presente, **se omite la comprobación de claim `modules`** — el módulo
 *    se gobierna solo por rol (típico para admin/billing del backoffice).
 */

export interface ModuleDefinition {
  id: string;
  i18nKey: string;
  path: string;
  icon: string;
  Component: LazyExoticComponent<ComponentType>;
  requiredRoles?: ReadonlyArray<string>;
}

export const moduleRegistry: ReadonlyArray<ModuleDefinition> = [
  {
    id: 'tracking',
    i18nKey: 'nav.tracking',
    path: '/tracking',
    icon: '📍',
    Component: lazy(() => import('@/modules/tracking/TrackingPage')),
  },
  {
    id: 'sensors',
    i18nKey: 'nav.sensors',
    path: '/sensors',
    icon: '🌡️',
    Component: lazy(() => import('@/modules/sensors/SensorsPage')),
  },
  {
    id: 'admin',
    i18nKey: 'nav.admin',
    path: '/admin',
    icon: '⚙️',
    requiredRoles: ['platform.admin'],
    Component: lazy(() => import('@/modules/admin/AdminPage')),
  },
  {
    id: 'billing',
    i18nKey: 'nav.billing',
    path: '/billing',
    icon: '💳',
    requiredRoles: ['tenant.admin', 'platform.admin'],
    Component: lazy(() => import('@/modules/billing/BillingPage')),
  },
];

/**
 * Devuelve los módulos visibles para el usuario:
 *  - Si el módulo declara `requiredRoles`: visible si el user tiene alguno de esos roles.
 *  - Si NO declara `requiredRoles`: visible si su `id` está en `userModules`.
 */
export function getEnabledModules(
  userModules: ReadonlyArray<string>,
  userRoles: ReadonlyArray<string> = [],
): ModuleDefinition[] {
  return moduleRegistry.filter((m) => {
    if (m.requiredRoles && m.requiredRoles.length > 0) {
      return m.requiredRoles.some((r) => userRoles.includes(r));
    }
    return userModules.includes(m.id);
  });
}
